/**
 * DetailsMachines — machine details tab (general info, manuals, technical specs)
 * Rewritten: no Mantine, uses design tokens, matches app design language.
 */
import React, { useEffect, useState } from 'react';
import client from '../API/API';
import { PageLoader } from '../components/ui/Skeleton';
import { Icon, ICONS } from '../components/ui/Icon';

const TH = {
  fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--ink-400)',
};

function Section({ title, icon, children }) {
  return (
    <div style={{
      background: 'var(--bg-surface)', border: '1px solid var(--border-hair)',
      borderRadius: 'var(--r-lg)', overflow: 'hidden', boxShadow: 'var(--sh-sm)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '14px 20px', borderBottom: '1px solid var(--border-hair)',
        background: 'var(--bg-raised)',
      }}>
        {icon && (
          <div style={{
            width: 28, height: 28, borderRadius: 'var(--r-md)',
            background: 'var(--accent-soft)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon d={icon} size={14} color="var(--accent)" />
          </div>
        )}
        <div style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 14, color: 'var(--ink-900)' }}>
          {title}
        </div>
      </div>
      <div style={{ padding: '16px 20px' }}>{children}</div>
    </div>
  );
}

function KVRow({ label, value, last }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '160px 1fr',
      padding: '9px 0',
      borderBottom: last ? 'none' : '1px solid var(--border-hair)',
      alignItems: 'center',
    }}>
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-500)' }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500, color: 'var(--ink-900)' }}>{value ?? '—'}</div>
    </div>
  );
}

const DetailsMachines = ({ nodeid, machineid }) => {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    if (!nodeid && !machineid) { setLoading(false); return; }
    setLoading(true); setError('');
    client.get('/api/machine_details/', {
      params: { machine_id: machineid || nodeid },
    }).then(res => {
      setData(res.data);
    }).catch(err => {
      const status = err?.response?.status;
      if (status === 404) {
        setData({});  // no details configured — show empty state
      } else {
        setError(err?.response?.data?.error || 'Failed to load details');
      }
    }).finally(() => setLoading(false));
  }, [nodeid, machineid]);

  if (loading) return <PageLoader />;

  if (error) return (
    <div style={{
      background: 'var(--offline-bg)', color: 'var(--offline)',
      borderRadius: 'var(--r-md)', padding: '12px 16px', fontSize: 13,
    }}>{error}</div>
  );

  // Backend returns flat fields (machine_id, machine_name, owned_by, timezone)
  // Strip any nested keys so only display-worthy KV pairs are shown
  const { manuals_and_docs: manuals = [], technical_details: techRows = [], ...general } = data || {};
  const hasAny = Object.keys(general).length > 0 || manuals.length > 0 || techRows.length > 0;

  if (!hasAny) return (
    <div style={{ padding: '60px 0', textAlign: 'center' }}>
      <div style={{
        width: 48, height: 48, borderRadius: '50%', background: 'var(--bg-raised)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 16px', color: 'var(--ink-300)',
      }}>
        <Icon d={ICONS.machine} size={22} />
      </div>
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 14, fontWeight: 500, color: 'var(--ink-500)', marginBottom: 4 }}>
        No details configured
      </div>
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-300)' }}>
        Machine details and documentation will appear here once set up.
      </div>
    </div>
  );

  const generalEntries = Object.entries(general);
  const techCols = ['Item', 'Manufacturer', 'Model No.', 'Expiry Date'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* General details */}
      {generalEntries.length > 0 && (
        <Section title="General Details" icon={ICONS.machine}>
          {generalEntries.map(([k, v], i) => (
            <KVRow key={k} label={k.replace(/_/g, ' ')} value={String(v)} last={i === generalEntries.length - 1} />
          ))}
        </Section>
      )}

      {/* Manuals & Documents */}
      {manuals.length > 0 && (
        <Section title="Manuals & Documents" icon={ICONS.upload}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
            {manuals.map((doc, i) => (
              <a key={i} href={doc.FileUrl} download target="_blank" rel="noreferrer"
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 14px', borderRadius: 'var(--r-md)',
                  border: '1px solid var(--border-subtle)', background: 'var(--bg-raised)',
                  textDecoration: 'none', color: 'inherit', transition: 'all var(--t-fast)',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'}>
                <div style={{
                  width: 32, height: 32, borderRadius: 'var(--r-md)',
                  background: 'var(--accent-soft)', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', flexShrink: 0,
                }}>
                  <Icon d={ICONS.upload} size={16} color="var(--accent)" />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500, color: 'var(--ink-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.Filename}
                  </div>
                  <div style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--ink-400)' }}>Download</div>
                </div>
              </a>
            ))}
          </div>
        </Section>
      )}

      {/* Technical details table */}
      {techRows.length > 0 && (
        <Section title="Technical Specifications" icon={ICONS.machine}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-sans)', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--bg-raised)', borderBottom: '1px solid var(--border-subtle)' }}>
                  {techCols.map(c => (
                    <th key={c} style={{ ...TH, padding: '9px 12px', textAlign: 'left' }}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {techRows.map((row, i) => (
                  <tr key={i} style={{
                    borderBottom: i < techRows.length - 1 ? '1px solid var(--border-hair)' : 'none',
                    background: i % 2 === 1 ? 'rgba(0,0,0,0.012)' : 'transparent',
                  }}>
                    <td style={{ padding: '10px 12px', color: 'var(--ink-900)', fontWeight: 500 }}>{row.Item_Name || '—'}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--ink-700)' }}>{row.Manufacture_Name || '—'}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--ink-700)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{row.Manufacture_Model_No || '—'}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--ink-500)', fontSize: 12 }}>{row.Expiry_Date || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </div>
  );
};

export default DetailsMachines;
